import * as XLSX from "xlsx";
import {
  PORTFOLIO_UPLOAD_COLUMNS,
  PORTFOLIO_GENDERS,
  LEGACY_OUTSTANDING_AMOUNT_COLUMN,
  LEGACY_PORTFOLIO_NAME_COLUMN,
} from "../data/mock/dashboardData";
import { parseDueDate } from "../utils/calendarDate";
import { cleanGhanaPhone } from "../utils/ghanaPhone";

const ALL_COLUMNS = [
  ...PORTFOLIO_UPLOAD_COLUMNS,
  LEGACY_OUTSTANDING_AMOUNT_COLUMN,
  LEGACY_PORTFOLIO_NAME_COLUMN,
];

const HEADER_MAP = {
  portfolio_name: ["portfolio_name", "portfolio name", "portfolio", "book name", "group name"],
  first_name: [
    "first_name",
    "first name",
    "firstname",
    "given name",
    "debtor first name",
  ],
  last_name: [
    "last_name",
    "last name",
    "lastname",
    "surname",
    "family name",
    "debtor last name",
  ],
  customer_name: [
    "customer_name",
    "customer name",
    "customer",
    "collector",
    "collector debtor name",
    "debtor",
    "debtor name",
    "name",
  ],
  ghana_card_number: [
    "ghana_card_number",
    "ghana card number",
    "ghana card",
    "ghana_card",
    "national id",
    "national_id",
    "ghanacard",
  ],
  gender: ["gender", "sex"],
  date_of_birth: ["date_of_birth", "date of birth", "dob", "birth date", "birthdate"],
  customer_number: [
    "phone number",
    "phone_number",
    "customer_number",
    "customer number",
    "customer no",
    "cust no",
    "cust number",
    "client number",
    "customer phone",
    "customer mobile",
  ],
  phone: ["phone", "mobile number", "mobile", "contact", "msisdn", "mobile_number"],
  email: ["email", "email address", "e-mail"],
  country: ["country", "nation", "country of residence"],
  region: ["region", "region of residence", "location region"],
  exact_area: [
    "exact area",
    "exact_area",
    "area",
    "suburb",
    "neighbourhood",
    "neighborhood",
    "locality",
  ],
  gps_address: ["gps_address", "gps address", "gps", "address", "residential address", "home address"],
  account_number: [
    "account_number",
    "account number",
    "account no",
    "account",
    "acc no",
    "acc number",
  ],
  loan_amount: ["loan_amount", "loan amount", "principal", "disbursed amount", "facility amount"],
  due_date: ["due_date", "due date", "due", "maturity date", "payment due date"],
  last_payment_date: [
    "last_payment_date",
    "last payment date",
    "last payment",
    "date of last payment",
    "last paid date",
  ],
  amount_paid: ["amount_paid", "amount paid", "paid", "total paid", "payments received"],
  outstanding_balance: [
    "outstanding_balance",
    "outstanding balance",
    "outstanding_amount",
    "outstanding amount",
    "current balance or arrears",
    "current_balance_or_arrears",
    "amount owed",
    "amount_owed",
    "balance",
    "arrears",
    "outstanding",
    "debt",
    "owed",
  ],
  outstanding_amount: [
    "outstanding_amount",
    "outstanding amount",
    "current balance or arrears",
    "current_balance_or_arrears",
    "amount owed",
    "amount_owed",
  ],
};

const GHANA_CARD_RE = /^GHA-?\d{9}-?\d$/i;

function normalizeHeader(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function aliasesForColumn(col) {
  return [
    ...new Set([
      ...(HEADER_MAP[col.key] ?? []),
      normalizeHeader(col.key),
      normalizeHeader(col.label),
    ]),
  ];
}

function mapHeaders(rawHeaders) {
  const mapping = {};
  const normalized = rawHeaders.map(normalizeHeader);

  for (const col of ALL_COLUMNS) {
    const aliases = aliasesForColumn(col);
    const index = normalized.findIndex((h) => aliases.includes(h));
    if (index >= 0) mapping[col.key] = index;
  }

  if (mapping.outstanding_balance == null && mapping.outstanding_amount != null) {
    mapping.outstanding_balance = mapping.outstanding_amount;
  }

  return mapping;
}

function parseAmount(value) {
  if (value == null || value === "") return null;
  const cleaned = String(value).replace(/[₵,\s]/g, "");
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

function normalizeGhanaCard(value) {
  const raw = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  if (!raw) return null;
  const compact = raw.replace(/-/g, "");
  if (/^GHA\d{10}$/.test(compact)) {
    return `GHA-${compact.slice(3, 12)}-${compact.slice(12)}`;
  }
  if (GHANA_CARD_RE.test(raw)) {
    const digits = raw.replace(/[^0-9]/g, "");
    return `GHA-${digits.slice(0, 9)}-${digits.slice(9)}`;
  }
  return null;
}

function isInstructionRow(cells) {
  const first = String(cells?.[0] ?? "")
    .trim()
    .toLowerCase();
  return (
    first.startsWith("group:") ||
    first.startsWith("note:") ||
    first.startsWith("instructions") ||
    first.includes("assigned automatically")
  );
}

function validateRow(row, rowIndex, groupName) {
  const errors = [];
  const data = { ...row, rowIndex, group_name: groupName };

  if (!String(row.portfolio_name ?? "").trim() && groupName) {
    data.portfolio_name = groupName;
  }

  if (!row.outstanding_balance && row.outstanding_amount != null && row.outstanding_amount !== "") {
    row.outstanding_balance = row.outstanding_amount;
  }

  const legacyName = String(row.customer_name ?? "").trim();
  let firstName = String(row.first_name ?? "").trim();
  let lastName = String(row.last_name ?? "").trim();
  if (!firstName && !lastName && legacyName) {
    const parts = legacyName.split(/\s+/);
    firstName = parts[0] || "";
    lastName = parts.slice(1).join(" ");
  }
  if (firstName) {
    row.first_name = firstName;
    data.first_name = firstName;
  }
  if (lastName) {
    row.last_name = lastName;
    data.last_name = lastName;
  }
  if (firstName || lastName) {
    data.customer_name = `${firstName} ${lastName}`.trim();
  } else if (legacyName) {
    data.customer_name = legacyName;
  }

  for (const col of PORTFOLIO_UPLOAD_COLUMNS.filter((c) => c.required)) {
    if (!String(row[col.key] ?? "").trim()) {
      errors.push(`${col.label} is required`);
    }
  }

  if (row.ghana_card_number) {
    const card = normalizeGhanaCard(row.ghana_card_number);
    if (!card) {
      errors.push("Ghana Card Number must look like GHA-XXXXXXXXX-X");
    } else {
      data.ghana_card_number = card;
    }
  }

  if (row.gender) {
    const gender = String(row.gender).trim();
    const match = PORTFOLIO_GENDERS.find((g) => g.toLowerCase() === gender.toLowerCase());
    if (!match) errors.push(`Gender must be one of: ${PORTFOLIO_GENDERS.join(", ")}`);
    else data.gender = match;
  }

  if (row.date_of_birth !== "" && row.date_of_birth != null) {
    const dob = parseDueDate(row.date_of_birth);
    if (!dob) errors.push("Date of Birth must be DD/MM/YYYY or YYYY-MM-DD");
    else data.date_of_birth = dob;
  }

  if (row.customer_number) {
    const customerPhone = cleanGhanaPhone(row.customer_number);
    if (!customerPhone) {
      errors.push("Phone number must be a valid Ghana mobile number (e.g. 024XXXXXXX)");
    } else {
      data.customer_number = customerPhone;
    }
  }

  if (row.phone) {
    const phone = cleanGhanaPhone(row.phone);
    if (!phone) {
      errors.push("Mobile number must be a valid Ghana number (e.g. 024XXXXXXX)");
    } else {
      data.phone = phone;
    }
  }

  if (row.email) {
    const email = String(row.email).trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push("Email must be a valid email address");
    } else {
      data.email = email.toLowerCase();
    }
  }

  if (row.country) {
    data.country = String(row.country).trim();
  }

  if (row.region) {
    data.region = String(row.region).trim();
  }

  if (row.exact_area) {
    data.exact_area = String(row.exact_area).trim();
  }

  if (row.gps_address) {
    data.gps_address = String(row.gps_address).trim();
  }

  if (row.account_number) {
    data.account_number = String(row.account_number).trim();
  }

  const loanAmount = parseAmount(row.loan_amount);
  if (row.loan_amount !== "" && row.loan_amount != null) {
    if (loanAmount == null || loanAmount <= 0) {
      errors.push("Loan Amount must be a number greater than 0");
    } else {
      data.loan_amount = loanAmount;
    }
  }

  if (row.due_date !== "" && row.due_date != null) {
    const dueDate = parseDueDate(row.due_date);
    if (!dueDate) {
      errors.push("Due date must be DD/MM/YYYY or YYYY-MM-DD");
    } else {
      data.due_date = dueDate;
    }
  }

  if (row.last_payment_date !== "" && row.last_payment_date != null) {
    const lastPay = parseDueDate(row.last_payment_date);
    if (!lastPay) {
      errors.push("Last Payment Date must be DD/MM/YYYY or YYYY-MM-DD");
    } else {
      data.last_payment_date = lastPay;
    }
  }

  const amountPaid = parseAmount(row.amount_paid);
  if (row.amount_paid !== "" && row.amount_paid != null) {
    if (amountPaid == null || amountPaid < 0) {
      errors.push("Amount Paid must be a number greater than or equal to 0");
    } else {
      data.amount_paid = amountPaid;
    }
  } else {
    data.amount_paid = 0;
  }

  const outstanding = parseAmount(row.outstanding_balance);
  if (row.outstanding_balance !== "" && row.outstanding_balance != null) {
    if (outstanding == null || outstanding <= 0) {
      errors.push("Outstanding Balance must be a number greater than 0");
    } else {
      data.outstanding_balance = outstanding;
      data.outstanding_amount = outstanding;
    }
  }

  return { data, errors, valid: errors.length === 0 };
}

export async function parsePortfolioFile(file, { groupName } = {}) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheetName =
    workbook.SheetNames.find((name) => /debt|account|data|upload/i.test(name)) ??
    workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

  if (rows.length < 2) {
    throw new Error("The file is empty or has no data rows.");
  }

  const [headerRow, ...bodyRows] = rows;
  const headerMapping = mapHeaders(headerRow);
  const missingRequired = PORTFOLIO_UPLOAD_COLUMNS.filter(
    (c) => c.required && headerMapping[c.key] == null
  );

  if (missingRequired.length) {
    throw new Error(
      `Missing required columns: ${missingRequired.map((c) => c.label).join(", ")}`
    );
  }

  const parsed = [];
  const validRows = [];
  const errorRows = [];

  bodyRows.forEach((cells, i) => {
    const rowNum = i + 2;
    const isEmpty = cells.every((c) => String(c ?? "").trim() === "");
    if (isEmpty || isInstructionRow(cells)) return;

    const row = {};
    for (const col of ALL_COLUMNS) {
      const idx = headerMapping[col.key];
      row[col.key] = idx != null ? cells[idx] : "";
    }

    const result = validateRow(row, rowNum, groupName);
    parsed.push(result);

    if (result.valid) validRows.push(result.data);
    else errorRows.push({ ...result.data, errors: result.errors });
  });

  // Duplicate Account Numbers in one file are allowed — each row still creates
  // its own portfolio/case record. Same account reuses the debtor profile.
  if (parsed.length === 0) {
    throw new Error("No data rows found below the header row.");
  }

  const totalAmount = validRows.reduce(
    (s, r) => s + (r.outstanding_balance || r.outstanding_amount || 0),
    0
  );

  return {
    fileName: file.name,
    groupName,
    totalRows: parsed.length,
    validCount: validRows.length,
    errorCount: errorRows.length,
    validRows,
    errorRows,
    totalAmount,
    passRate: parsed.length ? (validRows.length / parsed.length) * 100 : 0,
  };
}

/** Download an Excel template that round-trips cleanly through validation. */
export function downloadPortfolioTemplate(groupName) {
  const headers = PORTFOLIO_UPLOAD_COLUMNS.map((c) => c.label);
  // Sample account for the template row (duplicates are allowed on real uploads).
  const sampleAccount = `ACC-${Date.now().toString().slice(-8)}`;
  // Keep this array 1:1 with PORTFOLIO_UPLOAD_COLUMNS order.
  const sample = [
    "Kofi", // First Name
    "Mensah", // Last Name
    "GHA-123456789-0", // Ghana Card Number
    "Male", // Gender
    "1990-05-12", // Date of Birth
    "0241234567", // Phone Number
    "0247654321", // Mobile Number
    "kofi.mensah@example.com", // Email
    "Ghana", // Country
    "Greater Accra", // Region
    "Accra Central", // Exact Area
    "GR-8232-32", // GPS Address (GhanaPost)
    sampleAccount, // Account Number
    15000, // Loan Amount
    "2026-03-15", // Due Date
    "2026-02-01", // Last Payment Date
    2500, // Amount Paid
    12500, // Outstanding Balance
  ];

  if (sample.length !== headers.length) {
    throw new Error(
      `Template sample row has ${sample.length} values but ${headers.length} columns — update downloadPortfolioTemplate().`
    );
  }

  const dataSheet = XLSX.utils.aoa_to_sheet([headers, sample]);
  dataSheet["!cols"] = headers.map(() => ({ wch: 24 }));

  const instructions = [
    ["SimCollect portfolio upload template"],
    [""],
    ["Group", groupName || "(selected on upload)"],
    ["", "One sheet only — columns follow Individual KYC → Loan KYC → Payment KYC."],
    [""],
    [
      "Individual KYC",
      "First Name, Last Name, Ghana Card, Gender, DOB, Phone Number, Mobile Number, Email, Country, Region, Exact Area, GPS Address",
    ],
    ["Loan KYC", "Account Number, Loan Amount, Due Date"],
    ["Payment KYC", "Last Payment Date, Amount Paid, Outstanding Balance"],
    [""],
    ["Required columns", PORTFOLIO_UPLOAD_COLUMNS.filter((c) => c.required).map((c) => c.label).join(", ")],
    [""],
    ["Rules"],
    ["1", "One account row per debt facility. Empty rows are skipped."],
    [
      "2",
      "Account Numbers may repeat in the same file. Each row creates its own case; the same account shares one customer profile.",
    ],
    ["3", "Ghana Card format: GHA-XXXXXXXXX-X"],
    ["4", "Phone number and mobile: Ghana format e.g. 024XXXXXXX or 233XXXXXXXXX."],
    ["5", "GPS Address: GhanaPost format e.g. GR-8232-32 (optional)."],
    ["6", "Dates: DD/MM/YYYY or YYYY-MM-DD."],
    ["7", "Loan Amount and Outstanding Balance must be greater than 0."],
    ["8", "Gender: Male, Female, or Other."],
    ["9", "Country, Region, and Exact Area are required."],
    ["10", "Enter First Name and Last Name in separate columns. Do not combine them into one full name."],
    [""],
    ["Use the “Debt Accounts” sheet for your data. Do not rename the header row."],
  ];
  const helpSheet = XLSX.utils.aoa_to_sheet(instructions);
  helpSheet["!cols"] = [{ wch: 28 }, { wch: 80 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, dataSheet, "Debt Accounts");
  XLSX.utils.book_append_sheet(wb, helpSheet, "Instructions");

  const suffix = groupName ? groupName.replace(/\s+/g, "_").slice(0, 24) : "Template";
  XLSX.writeFile(wb, `SimCollect_${suffix}.xlsx`);
}

/**
 * Build a cleaned .xlsx from validated rows (normalised phones, amounts, dates).
 * Used so the backend only receives pre-validated data.
 */
export function buildCleanedPortfolioFile(parseResult, originalFileName = "portfolio.xlsx") {
  if (!parseResult?.validRows?.length) {
    throw new Error("No valid rows to upload. Fix the Excel file and try again.");
  }
  if (parseResult.errorCount > 0) {
    throw new Error(
      `${parseResult.errorCount} row${parseResult.errorCount === 1 ? "" : "s"} failed validation. Fix them in Excel before uploading.`
    );
  }

  const headers = PORTFOLIO_UPLOAD_COLUMNS.map((col) => col.label);
  const body = parseResult.validRows.map((row) =>
    PORTFOLIO_UPLOAD_COLUMNS.map((col) => {
      const value = row[col.key];
      return value == null ? "" : value;
    })
  );

  const sheet = XLSX.utils.aoa_to_sheet([headers, ...body]);
  sheet["!cols"] = headers.map(() => ({ wch: 24 }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Debt Accounts");

  const arrayBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const baseName = String(originalFileName || "portfolio.xlsx").replace(/\.[^.]+$/, "");
  return new File([arrayBuffer], `${baseName}-cleaned.xlsx`, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function simulatePortfolioImport(result) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        uploadId: `UPL-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        accounts: result.validCount,
        totalOwed: result.totalAmount,
        passRate: result.passRate,
      });
    }, 1400);
  });
}
